"use client";

import { usePostHog } from "@posthog/react/slim";
import { Link } from "@tanstack/react-router";
import { Logo } from "./logo";
import { useState } from "react";
import { toast } from "sonner";

import { authClient } from "@/lib/auth-client";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@super-resume-new/ui/components/dialog";
import { Spinner } from "@super-resume-new/ui/components/spinner";

export interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "signin" | "signup";
  title?: string;
  description?: string;
  callbackURL?: string;
  onSuccess?: () => void;
}

export function AuthDialog({
  open,
  onOpenChange,
  title,
  description,
  callbackURL = "/",
  onSuccess,
}: AuthDialogProps) {
  const posthog = usePostHog();
  const [socialLoading, setSocialLoading] = useState<string | null>(null);

  const handleSocialLogin = async (provider: "google" | "github" | "linkedin" | "twitter") => {
    posthog.capture("oauth_sign_in_started", { provider });
    setSocialLoading(provider);
    try {
      await authClient.signIn.social({
        provider,
        callbackURL,
      });
      onSuccess?.();
    } catch (err) {
      console.error(`${provider} sign in failed:`, err);
      toast.error(`Failed to sign in with ${provider === "twitter" ? "X" : provider}`);
      setSocialLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader className="text-center sm:text-center">
          <Logo className="mx-auto mb-3 size-12 rounded-md border border-brand/20" />
          <DialogTitle className="text-xl font-semibold text-foreground">
            {title || "Get Started with JobCues"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {description || (
              <>
                Start optimizing for job applications with <br />
                <span className="font-semibold text-brand-foreground">3 free rewrites.</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogPanel>
          {/* Social OAuth Providers */}
          <div className="flex flex-col gap-2.5">
            {/* Google */}
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center h-10 text-xs font-semibold gap-2.5 border-border hover:bg-muted/80 cursor-pointer"
              onClick={() => handleSocialLogin("google")}
              disabled={!!socialLoading}
            >
              {socialLoading === "google" ? (
                <Spinner className="size-4" />
              ) : (
                <svg className="size-4 shrink-0" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
              )}
              Continue with Google
            </Button>

            {/* LinkedIn */}
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center h-10 text-xs font-semibold gap-2.5 border-border hover:bg-muted/80 cursor-pointer"
              onClick={() => handleSocialLogin("linkedin")}
              disabled={!!socialLoading}
            >
              {socialLoading === "linkedin" ? (
                <Spinner className="size-4" />
              ) : (
                <svg className="size-4 shrink-0 fill-[#0A66C2]" viewBox="0 0 24 24">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 8.76a1.64 1.64 0 1 0 0-3.28 1.64 1.64 0 0 0 0 3.28M7.86 18.5v-8.37H5.06v8.37z" />
                </svg>
              )}
              Continue with LinkedIn
            </Button>

            {/* GitHub */}
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center h-10 text-xs font-semibold gap-2.5 border-border hover:bg-muted/80 cursor-pointer"
              onClick={() => handleSocialLogin("github")}
              disabled={!!socialLoading}
            >
              {socialLoading === "github" ? (
                <Spinner className="size-4" />
              ) : (
                <svg className="size-4 shrink-0 fill-current" viewBox="0 0 24 24">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                  />
                </svg>
              )}
              Continue with GitHub
            </Button>

            {/* X (Twitter) */}
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center h-10 text-xs font-semibold gap-2.5 border-border hover:bg-muted/80 cursor-pointer"
              onClick={() => handleSocialLogin("twitter")}
              disabled={!!socialLoading}
            >
              {socialLoading === "twitter" ? (
                <Spinner className="size-4" />
              ) : (
                <svg className="size-4 shrink-0 fill-current" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              )}
              Continue with X
            </Button>


          </div>

          {/* Terms & Privacy Policy Notice */}
          <p className="mt-4 text-center text-xs text-muted-foreground leading-relaxed">
            By continuing, you agree to our{" "}
            <Link
              to="/terms"
              onClick={() => onOpenChange(false)}
              className="underline underline-offset-4 font-medium text-foreground hover:text-primary transition-colors"
            >
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              onClick={() => onOpenChange(false)}
              className="underline underline-offset-4 font-medium text-foreground hover:text-primary transition-colors"
            >
              Privacy Policy
            </Link>
            .
          </p>

          {/*
            NOTE: Username/Email & Password auth is commented out for now.
            We are using provider-based authentication (Google, GitHub, X, LinkedIn).
          */}
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
